/* eslint-disable max-lines */
/** biome-ignore-all lint/complexity/useLiteralKeys: more work */
/**
 * State Sanitizer & Migration System
 *
 * Provides robust validation, sanitization, and recovery for persisted NGXS states.
 * When the application loads persisted state from localStorage, old or malformed data
 * can cause runtime errors (e.g. accessing properties on undefined, missing required fields).
 *
 * This module:
 * 1. Validates each state slice against its expected schema
 * 2. Fills missing properties with defaults
 * 3. Removes unknown/stale properties where strict schemas are applied
 * 4. Resets deeply corrupted states to safe defaults
 * 5. Provides the `afterDeserialize` hook for the NGXS storage plugin
 *    Note: some nested objects intentionally preserve extra keys (e.g. via deepMergeDefaults)
 *    to support forward compatibility.
 */

import { SettingsPageTab, type SettingsStateModel, type ZoomSliderMode } from 'commons';
import type { EChartsOption } from 'echarts';
import type { KPI_Table } from '../../components/dtis-multilane-viewer/KPI_Table';
import type { AnomaliesStateModel } from './anomalies/anomalies.state';
import type { MultiViewStateModel } from './multi-view/MultiViewStateModel';
import { SingleView } from './multi-view/SingleView';
import { echartsDefaultValue } from './settings/echartsDefaultValue';
import { deepMergeDefaults, ensureArray, ensureBoolean, ensureNumber, ensureString, parseDate } from './state-sanitizer-helpers';
import { TEST_PHASE_STATE_DEFAULTS, type TestPhaseStateModel } from './test-phase/test-phase.state.model';

/** Current schema version — bump when any state shape changes */
export const STATE_SCHEMA_VERSION = 1;
const SCHEMA_VERSION_KEY = '__dtis_state_schema_version';

// ─────────────────────────────────────────────────────────────────────────────
// Settings State Sanitizer
// ─────────────────────────────────────────────────────────────────────────────

const SETTINGS_DEFAULTS: SettingsStateModel = {
  filterPanelVisibility: false,
  debug: false,
  isAdmin: false,
  search: '',
  enabledAutoFresh: [],
  pollingInterval: 30000,
  databrowserIsCollapsed: false,
  currentSettingPageTab: SettingsPageTab.APPLICATION,
  bu_settings: {},
  sidebarExpanded: false,
  language: 'os',
  theme: 'light',
  resolutionMultiplier: 1,
  echartsConfig: echartsDefaultValue,
};

function sanitizeSettingsState(state: unknown): SettingsStateModel {
  if (!state || typeof state !== 'object') {
    console.warn('[StateSanitizer] settings_state is invalid, resetting to defaults');
    return { ...SETTINGS_DEFAULTS, echartsConfig: { ...echartsDefaultValue } };
  }

  const s = state as Record<string, unknown>;

  return {
    filterPanelVisibility: ensureBoolean(s['filterPanelVisibility'], SETTINGS_DEFAULTS.filterPanelVisibility),
    debug: ensureBoolean(s['debug'], SETTINGS_DEFAULTS.debug),
    isAdmin: ensureBoolean(s['isAdmin'], SETTINGS_DEFAULTS.isAdmin),
    search: ensureString(s['search'], ''),
    enabledAutoFresh: ensureArray(s['enabledAutoFresh'], []),
    pollingInterval: ensureNumber(s['pollingInterval'], SETTINGS_DEFAULTS.pollingInterval, 1000),
    databrowserIsCollapsed: ensureBoolean(s['databrowserIsCollapsed'], SETTINGS_DEFAULTS.databrowserIsCollapsed),
    currentSettingPageTab: sanitizeSettingsPageTab(s['currentSettingPageTab']),
    bu_settings: sanitizeBuSettings(s['bu_settings']),
    sidebarExpanded: ensureBoolean(s['sidebarExpanded'], SETTINGS_DEFAULTS.sidebarExpanded),
    language: sanitizeLanguageCode(s['language']),
    theme: sanitizeTheme(s['theme']),
    resolutionMultiplier: ensureNumber(s['resolutionMultiplier'], SETTINGS_DEFAULTS.resolutionMultiplier, 0.25, 4),
    echartsConfig: sanitizeEchartsConfig(s['echartsConfig']),
  };
}

function sanitizeSettingsPageTab(value: unknown): SettingsPageTab {
  if (typeof value === 'number' && value >= 0 && value <= 2) {
    return value;
  }
  return SettingsPageTab.APPLICATION;
}

function sanitizeLanguageCode(value: unknown): 'en' | 'de' | 'os' {
  if (value === 'en' || value === 'de' || value === 'os') {
    return value;
  }
  return 'os';
}

function sanitizeTheme(value: unknown): 'light' | 'dark' {
  if (value === 'light' || value === 'dark') {
    return value;
  }
  return 'light';
}

function sanitizeBuSettings(value: unknown): { [bu_id: string]: any } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const result: Record<string, any> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (val && typeof val === 'object') {
      const buSetting = val as Record<string, unknown>;
      result[key] = {
        labels: {
          available: ensureArray((buSetting['labels'] as Record<string, unknown>)?.['available'], []),
        },
      };
    }
  }
  return result;
}

function sanitizeEchartsConfig(value: unknown): typeof echartsDefaultValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...echartsDefaultValue };
  }
  return deepMergeDefaults(value, echartsDefaultValue);
}

// ─────────────────────────────────────────────────────────────────────────────
// MultiView State Sanitizer
// ─────────────────────────────────────────────────────────────────────────────

const MULTI_VIEW_DEFAULTS: MultiViewStateModel = {
  views: [],
  currentViewId: undefined,
  tableLaneFilter: undefined,
  historyPanelOpen: false,
};

function sanitizeMultiViewState(state: unknown): MultiViewStateModel {
  if (!state || typeof state !== 'object') {
    console.warn('[StateSanitizer] multi_view_state is invalid, resetting to defaults');
    return { ...MULTI_VIEW_DEFAULTS };
  }

  const s = state as Record<string, unknown>;
  const views = ensureArray<unknown>(s['views'], []);
  const sanitizedViews = views.map((v) => sanitizeSingleView(v)).filter((v): v is SingleView => v !== null);

  const currentViewId = typeof s['currentViewId'] === 'string' ? s['currentViewId'] : undefined;
  const validCurrentViewId = currentViewId && sanitizedViews.some((v) => v.id === currentViewId) ? currentViewId : sanitizedViews[0]?.id;

  return {
    views: sanitizedViews,
    currentViewId: validCurrentViewId,
    tableLaneFilter: typeof s['tableLaneFilter'] === 'string' ? s['tableLaneFilter'] : undefined,
    historyPanelOpen: false,
  };
}

function sanitizeDateRange(value: unknown): { start_date: Date; end_date: Date } | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const dr = value as Record<string, unknown>;
  const startDate = parseDate(dr['start_date']);
  const endDate = parseDate(dr['end_date']);
  return startDate && endDate ? { start_date: startDate, end_date: endDate } : undefined;
}

/**
 * Applies optional / conditionally-present fields from raw storage data onto
 * a partially-constructed SingleView. Extracted to keep `sanitizeSingleView`
 * below the allowed cognitive-complexity threshold.
 */
function applyOptionalSingleViewFields(v: Record<string, unknown>, sanitized: Partial<SingleView>, templateView: SingleView): void {
  const dateRange = sanitizeDateRange(v['dateRange']);
  if (dateRange) {
    sanitized.dateRange = dateRange;
  }

  if (v['styles'] && typeof v['styles'] === 'object') {
    sanitized.styles = deepMergeDefaults(v['styles'], templateView.styles);
  }

  if (typeof v['zoomSliderMode'] === 'number') {
    sanitized.zoomSliderMode = v['zoomSliderMode'] as unknown as ZoomSliderMode;
  }

  const lastVisited = parseDate(v['lastVisited'] ?? v['lastVisted']);
  if (lastVisited) {
    sanitized.lastVisted = lastVisited;
  }

  if (v['echartsConfig'] && typeof v['echartsConfig'] === 'object') {
    sanitized.echartsConfig = v['echartsConfig'] as EChartsOption;
  }

  if (v['kpiTable'] && typeof v['kpiTable'] === 'object') {
    sanitized.kpiTable = v['kpiTable'] as KPI_Table;
  }
}

function sanitizeSingleView(value: unknown): SingleView | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  try {
    const v = value as Record<string, unknown>;
    const templateView = new SingleView();
    const sanitized: Partial<SingleView> = {
      id: ensureString(v['id'], ''),
      owner: ensureString(v['owner'], ''),
      label: ensureString(v['label'], 'Untitled'),
      sharedWith: ensureArray(v['sharedWith'], []),
      isLoading: ensureArray(v['isLoading'], []),
      commentModeEnabled: ensureBoolean(v['commentModeEnabled'], false),
      isLoadingComments: ensureBoolean(v['isLoadingComments'], false),
      testPhasesVisible: ensureBoolean(v['testPhasesVisible'], false),
      lanes: ensureArray(v['lanes'], templateView.lanes),
      sensorTable: ensureArray(v['sensorTable'], []),
      comments: ensureArray(v['comments'], []),
      zoomState: ensureArray(v['zoomState'], templateView.zoomState),
      pageType: sanitizePageType(v['pageType']),
      tableViewLaneFilter: ensureString(v['tableViewLaneFilter'], 'all'),
      // Per-view undo/redo stacks are intentionally never persisted.
      history: [],
      historyMeta: [],
      future: [],
      futureMeta: [],
    };

    applyOptionalSingleViewFields(v, sanitized, templateView);

    if (!sanitized.id) {
      return null;
    }
    return new SingleView(sanitized);
  } catch (err) {
    console.warn('[StateSanitizer] Failed to sanitize a SingleView, skipping', err);
    return null;
  }
}

function sanitizePageType(value: unknown): 'linechart' | 'table' | 'json' {
  if (value === 'linechart' || value === 'table' || value === 'json') {
    return value;
  }
  return 'linechart';
}

// ─────────────────────────────────────────────────────────────────────────────
// Test Phase State Sanitizer
// ─────────────────────────────────────────────────────────────────────────────

function sanitizeTestPhaseState(state: unknown): TestPhaseStateModel {
  if (!state || typeof state !== 'object') {
    return { ...TEST_PHASE_STATE_DEFAULTS };
  }

  const s = state as Record<string, unknown>;
  return {
    phases: ensureArray(s['phases'], []).filter(isValidTestPhaseItem),
    hiddenPhaseIds: ensureArray<string>(s['hiddenPhaseIds'], []).filter((id) => typeof id === 'string'),
    hiddenPhaseValues: ensureArray<number>(s['hiddenPhaseValues'], []).filter((v) => typeof v === 'number'),
    isLoading: ensureBoolean(s['isLoading'], false),
    error: typeof s['error'] === 'string' ? s['error'] : null,
  };
}

function isValidTestPhaseItem(item: unknown): boolean {
  if (!item || typeof item !== 'object') {
    return false;
  }
  const i = item as Record<string, unknown>;
  return typeof i['identifier'] === 'string' && typeof i['phaseName'] === 'string' && typeof i['color'] === 'string' && typeof i['visible'] === 'boolean';
}

// ─────────────────────────────────────────────────────────────────────────────
// Lightweight state sanitizers (mostly transient data)
// ─────────────────────────────────────────────────────────────────────────────

function sanitizeTelemetryState(state: unknown): Record<string, unknown> {
  if (!state || typeof state !== 'object') {
    return { selectedTwin: null, currentFilterValue: '', data: [] };
  }
  const s = state as Record<string, unknown>;
  return {
    selectedTwin: s['selectedTwin'] ?? null,
    currentFilterValue: ensureString(s['currentFilterValue'], ''),
    data: ensureArray(s['data'], []),
  };
}

function sanitizeAnomaliesState(state: unknown): AnomaliesStateModel {
  const s = state && typeof state === 'object' ? (state as Record<string, unknown>) : {};
  return { selectedTwinId: typeof s['selectedTwinId'] === 'string' ? s['selectedTwinId'] : null };
}

function sanitizeDeviceStatusState(state: unknown): Record<string, unknown> {
  const s = state && typeof state === 'object' ? (state as Record<string, unknown>) : {};
  return { deviceStatus: ensureArray(s['deviceStatus'], []) };
}

// ─────────────────────────────────────────────────────────────────────────────
// Main entry point: afterDeserialize hook
// ─────────────────────────────────────────────────────────────────────────────

const STATE_SANITIZERS: Record<string, (state: unknown) => unknown> = {
  settings_state: sanitizeSettingsState,
  multi_view_state: sanitizeMultiViewState,
  test_phase_state: sanitizeTestPhaseState,
  telemetry_state: sanitizeTelemetryState,
  anomalies_state: sanitizeAnomaliesState,
  deviceStatus: sanitizeDeviceStatusState,
};

/**
 * NGXS Storage Plugin `afterDeserialize` hook.
 *
 * Handles individual state keys and the combined `@@STATE` used by `keys: '*'`.
 * Called after the raw JSON is parsed from storage.
 * Validates and sanitizes state, replacing corrupt/missing data with safe defaults.
 */
export function sanitizeStateAfterDeserialize(obj: unknown, key: string): unknown {
  if (key === '@@STATE' && obj && typeof obj === 'object' && !Array.isArray(obj)) {
    return Object.fromEntries(Object.entries(obj).map(([stateKey, state]) => [stateKey, sanitizeStateAfterDeserialize(state, stateKey)]));
  }
  const sanitizer = STATE_SANITIZERS[key];
  if (!sanitizer) {
    return obj;
  }

  try {
    return sanitizer(obj);
  } catch (err) {
    console.error(`[StateSanitizer] Critical error sanitizing "${key}", resetting to defaults.`, err);
    return undefined;
  }
}

/**
 * Checks if a full state reset is needed by comparing schema versions.
 * Call this on app startup to detect large-scale schema changes.
 */
export function checkAndMigrateSchemaVersion(): boolean {
  try {
    const storedVersion = localStorage.getItem(SCHEMA_VERSION_KEY);
    const currentVersion = STATE_SCHEMA_VERSION.toString();

    if (storedVersion === currentVersion) {
      return false;
    }

    if (storedVersion === null) {
      localStorage.setItem(SCHEMA_VERSION_KEY, currentVersion);
      return false;
    }

    console.warn(`[StateSanitizer] Schema version changed: ${storedVersion} → ${currentVersion}. State will be sanitized.`);
    localStorage.setItem(SCHEMA_VERSION_KEY, currentVersion);
    return true;
  } catch {
    return false;
  }
}
